<template>
    <loading-popup v-if="showLoader || isLoading" />
    <session-timeout-modal />
    <browser-navigation-modal />
    <start-date-is-invalid-modal v-if="!isOfflineQuote" />
    <quote-expired-modal />
    <quote-accepted-modal />
    <not-quoted-modal />
    <additional-assistance-modal />
    <a
        href="#main-content"
        @click.prevent="skipToMainContent"
        class="sr-only focus:not-sr-only focus:absolute focus:top-1.5 focus:left-1.5 bg-black text-white px-2 py-2 z-50 no-underline rounded font-bold"
        >Skip to main content</a
    >
    <asli-popup :show-close-button="false" v-model="displayInsurerModal" class="p-4" header="Please Review">
        <div class="py-4">
            <p>
                The new best available An Post Insurance price now comes from {{ insurerName }}. Benefits, Cover Options
                and Limits have changed. You'll need to review your options to make sure you get the cover you need.
            </p>
        </div>
        <div class="px-4 py-4 flex gap-x-2 justify-start flex-col xs:flex-row gap-y-2 xs:gap-y-0">
            <asli-button id="extend-session" color="primary" @click="closeInsurerModal">Continue</asli-button>
        </div>
    </asli-popup>
    <div class="min-h-screen flex flex-col">
        <div class="grow">
            <asli-header
                :header-image="{ src: anPostLogo, alt: 'An Post Insurance', class: 'w-44' }"
                :showPhoneNumberButton="true"
                :phoneButtonHref="`tel:${removeSpacesAndParentheses(BRAND_PHONE_NUMBER)}`"
                :phoneButtonText="BRAND_PHONE_NUMBER"
                :isMobile="isMobile"
                backgroundColor="bg-white"
                btnBackgroundColor="transparent"
            >
            </asli-header>
            <nav class="w-full h-auto bg-white flex justify-center align-items-end" aria-label="Primary navigation">
                <div class="max-w-[935px] px-[30px] w-full box-content h-full">
                    <div class="flex justify-items-center sm:justify-items-end w-full h-full sm:pr-[27px] sm:-mt-6">
                        <div class="ml-auto flex flex-row gap-2 pb-2">
                            <asli-link :href="siteLinks.eligibilityCriteria" class="mr-0 md:mr-4 underline">
                                Eligibility Criteria
                            </asli-link>
                            <asli-link :href="siteLinks.termsOfBusiness" class="mr-0 md:mr-4 underline">
                                Terms of Business
                            </asli-link>
                            <asli-link :href="siteLinks.faqs" class="underline">FAQs</asli-link>
                        </div>
                    </div>
                </div>
            </nav>
            <div class="w-full bg-[#e8f2df] py-5">
                <div class="flex items-center max-w-[935px] px-[30px] m-auto h-full">
                    <div class="flex flex-col flex-1 sm:flex-row justify-between items-center gap-3">
                        <div class="flex flex-row items-center text-center">
                            <h1 class="text-[32px] text-left font-bold text-primary leading-[25px]">Home Insurance</h1>
                        </div>
                        <div class="flex align-items-end">
                            <!-- TrustBox widget - Horizontal -->
                            <div
                                class="trustpilot-widget"
                                data-locale="en-US"
                                data-template-id="5406e65db0d04a09e042d5fc"
                                data-businessunit-id="5e26d95c79ac4d0001a5cd8e"
                                data-style-height="28px"
                                data-style-width="100%"
                                inert
                            >
                                <span class="sr-only">Trustpilot reviews</span>
                            </div>
                            <!-- End TrustBox widget -->
                        </div>
                    </div>
                </div>
            </div>
            <main id="main-content" tabindex="-1">
                <asli-progress-bar
                    v-if="!hideProgressBar"
                    class="mt-[10px] sm:mt-[46px]"
                    :steps="steps"
                    :currentStep="currentStep"
                ></asli-progress-bar>
                <asli-page-layout>
                    <asli-panel v-if="showAssumptionPanel" background="bg-panel-primary" class="my-7 rounded-[5px] p-5">
                        <p class="mb-[10px] font-bold">
                            {{ AssumptionsPanel.heading }}
                        </p>
                        <p class="text-sm">
                            {{ AssumptionsPanel.description }}
                        </p>
                    </asli-panel>
                    <asli-divider v-if="!hideProgressBar" class="mt-[30px]" />
                    <template v-if="showView"> <router-view></router-view> </template>
                </asli-page-layout>
            </main>
        </div>
        <asli-footer class="mt-auto">
            <asli-page-layout class="py-8 text-white text-sm">
                <p class="mb-8">© An Post Insurance {{ currentYear }}</p>
                <p>
                    One Direct (Ireland) Limited, trading as An Post Insurance, is regulated by the Central Bank of
                    Ireland. One Direct (Ireland) Limited is a wholly owned subsidiary of An Post.
                </p>
                <p>Registered in Ireland Number 452399. Registered Office: GPO, O'Connell St., Dublin 1. D01 F5P2</p>
            </asli-page-layout>
        </asli-footer>
    </div>
    <additional-assistance-widget />
</template>

<script setup lang="ts">
import {
    AsliHeader,
    AsliProgressBar,
    AsliPageLayout,
    AsliDivider,
    AsliPanel,
    Step,
    initUI,
    anPostTheme,
    AsliFooter,
    AsliLink,
    AsliPopup,
    AsliButton
} from "@mbs-npm/anpost-ui";
import anPostLogo from "@/assets/anpost-logo-green.png";
import { useMainStore } from "./stores/main";
import { computed, onMounted, ref, watch } from "vue";
import { useListStore } from "./stores/lists/lists";
import { goToError } from "@/utils/error-handling";
import { useRouter } from "vue-router";
import { getValidQuotes, useProposalStore } from "./stores/risk/risk";
import { ROUTE_NAMES } from "./router/types";
import LoadingPopup from "./components/LoadingPopup.vue";
import { routeGuard } from "./router";
import { AssumptionsPanel } from "./utils/labels";
import SessionTimeoutModal from "./components/SessionTimeoutModal.vue";
import { removeSpacesAndParentheses } from "@/utils/index";
import { BRAND_PHONE_NUMBER } from "@/static/constants";
import { siteLinks } from "./utils/links";
import StartDateIsInvalidModal from "@/components/StartDateIsInvalidModal.vue";
import QuoteExpiredModal from "@/components/QuoteExpiredModal.vue";
import QuoteAcceptedModal from "@/components/QuoteAcceptedModal.vue";
import NotQuotedModal from "@/components/NotQuotedModal.vue";
import dayjs from "dayjs";
import BrowserNavigationModal from "@/components/BrowserNavigationModal.vue";
import AdditionalAssistanceModal from "@/components/AdditionalAssistance/AdditionalAssistanceModal.vue";
import AdditionalAssistanceWidget from "@/components/AdditionalAssistance/AdditionalAssistanceWidget.vue";

initUI(anPostTheme);

const mainStore = useMainStore();
const proposalStore = useProposalStore();
const listsStore = useListStore();
const router = useRouter();

const isLoading = computed<boolean>(() => {
    return mainStore.isLoading;
});

const isOfflineQuote = computed(() => proposalStore.proposal?.recalled_from_offline);

const hideProgressBar = computed(() => {
    const hiddenRoutes = [
        ROUTE_NAMES.QuoteRecall,
        ROUTE_NAMES.MarketingPreferences,
        ROUTE_NAMES.MarketingPreferencesLogin,
        ROUTE_NAMES.MarketingPreferencesSuccess
    ];
    return hiddenRoutes.includes((router.currentRoute.value.name as ROUTE_NAMES) ?? "");
});

const currentStep = computed(() => mainStore.appStep);
const showAssumptionPanel = computed(() => router.currentRoute.value.name === ROUTE_NAMES.Marketing);
const currentYear = dayjs().get("year");

const steps: Array<Step> = [
    {
        label: "About You",
        icon: "user"
    },
    {
        label: "Your Property",
        icon: "home"
    },
    {
        label: "Your Cover",
        icon: "coverShield"
    },
    {
        label: "Quotes",
        icon: "list"
    },
    {
        label: "Payment",
        icon: "card"
    }
];

const showView = computed(() => proposalStore.gettingProposal === false && listsStore.gettingLists === false);
const showLoader = computed(() => proposalStore.gettingProposal === true || listsStore.gettingLists === true);

onMounted(async () => {
    await router.isReady();
    const queryUuid = router.currentRoute.value.query?.uuid ?? "";

    try {
        await listsStore.getLists();

        if (queryUuid) await proposalStore.getProposal(queryUuid.toString());
        else {
            proposalStore.gettingProposal = false;
            routeGuard();
        }
    } catch (error) {
        goToError(queryUuid as string);
    }
});

let windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value <= 768);

onMounted(() => {
    window.addEventListener("resize", () => {
        windowWidth.value = window.innerWidth;
    });
});

// logic for displaying a modal when the insurer has changed
const proposal = computed(() => proposalStore?.proposal);
const quote = computed(() => proposal.value?.quote);
const displayInsurerModal = ref(false);
const quoteSchemeChanged = computed(() => proposal.value.quote_scheme_changed);
const insurerName = computed(() => {
    if (quote.value.insurer_name?.slice(-1) === ".") {
        return quote.value.insurer_name.slice(0, -1);
    }
    return quote.value.insurer_name;
});

function closeInsurerModal() {
    displayInsurerModal.value = false;
}

function skipToMainContent() {
    const mainContent = document.getElementById("main-content");
    if (mainContent) {
        mainContent.focus();
        mainContent.scrollIntoView({ behavior: "smooth", block: "start" });
    }
}

watch(quoteSchemeChanged, (newVal, oldVal) => {
    const quotes = getValidQuotes(proposalStore.proposal.quotes);

    if (newVal === true && oldVal === false && quotes.length > 0) {
        displayInsurerModal.value = true;
    }
});
</script>
